/**
 * OpeningRegistry — single source of truth for which `<Opening*>` JSX
 * component renders for each top-level `openingAnimation` code.
 *
 * Adding a new opening means (1) writing the component, (2) registering
 * it here. Unknown codes fall back to `TheatreCurtain` because that's
 * the user-facing default (matching the legacy behaviour).
 */
import { TheatreCurtain } from './TheatreCurtain';
import { CinematicFade } from './CinematicFade';
import type { ComponentType } from 'react';

export type OpeningCode = 'cinematic-curtain' | 'cinematic-fade' | 'none';

export interface OpeningProps {
  /** True while the top-level opening animation is animating. Drives
   *  the parent's `curtainPending` flag — when it flips to true, the
   *  curtain layer either keeps mounted (for `done` collapse) or gets
   *  fully unmounted (for `none`). */
  active: boolean;
}

const OPENING_COMPONENTS: Record<string, ComponentType<OpeningProps>> = {
  'cinematic-curtain': TheatreCurtain,
  'cinematic-fade': CinematicFade,
  none: CinematicFade, // Fallback no-op layer; CinematicFade acts as a no-op when active=false.
};

export function resolveOpening(code: string | undefined): ComponentType<OpeningProps> {
  if (!code) return TheatreCurtain;
  return OPENING_COMPONENTS[code] ?? TheatreCurtain;
}

/** Helper: should the opening even be mounted for the given code? */
export function shouldMountOpening(code: string | undefined): boolean {
  if (!code) return true;
  return code !== 'none';
}
